import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Analytics } from '@mui/icons-material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ClassOutlinedIcon from '@mui/icons-material/ClassOutlined';
import ContactsIcon from '@mui/icons-material/ContactsOutlined';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import HistoryIcon from '@mui/icons-material/History';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import InsertLinkOutlinedIcon from '@mui/icons-material/InsertLinkOutlined';
import Inventory2Icon from '@mui/icons-material/Inventory2Outlined';
import ListAltRoundedIcon from '@mui/icons-material/ListAltRounded';
import LockIcon from '@mui/icons-material/LockOutlined';
import NotificationsIcon from '@mui/icons-material/NotificationsActiveOutlined';
import Paid from '@mui/icons-material/Paid';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PriceChangeOutlinedIcon from '@mui/icons-material/PriceChangeOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SdCardAlertOutlinedIcon from '@mui/icons-material/SdCardAlertOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';
import ThreeSixtyOutlined from '@mui/icons-material/ThreeSixtyOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Collapse } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Skeleton from '@mui/material/Skeleton';
import { styled, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import paths from '../../router/paths';
import { check } from '../../services/check-access';
import { useStateToProps } from '../../store/actions/actions';
import Logo from '../logo';

const expandedDrawerWidth = 240;
const collapsedDrawerWidth = 44;

// Add a styled component for consistent icon sizing
const StyledIcon = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  '& > *': {
    fontSize: '1.25rem',
  },
}));

// Update the StyledListItem component to show lock icon but not disable
const StyledListItem = styled(ListItem)(({ theme, isactive }) => ({
  color: theme.palette.text.secondaryLight,
  position: 'relative',
  minHeight: '40px',
  paddingLeft: '5px',
  '& .menuIcon': {
    color: theme.palette.text.secondaryLight,
  },
  '& .MuiListItemIcon-root': {
    minWidth: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '&:hover': {
    backgroundColor: theme.palette.text.primary,
    color: theme.palette.common.white,
    '& .menuIcon': {
      color: theme.palette.common.white,
    },
  },
  ...(isactive === 'true' && {
    backgroundColor: theme.palette.background.dark,
    color: theme.palette.common.white,
    '&:after': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      height: '100%',
      width: '3px',
      backgroundColor: theme.palette.primary.main,
    },
    '& .menuIcon': {
      color: theme.palette.common.white,
    },
    '&:hover': {
      backgroundColor: theme.palette.text.primary,
      color: theme.palette.common.white,
      '& .menuIcon': {
        color: theme.palette.common.white,
      },
    },
  }),
}));

const StyledSubMenuItem = styled(StyledListItem)(({ theme }) => ({
  padding: '2px 16px 2px 24px',
}));

const skeletonStyle = {
  bgcolor: 'rgba(255, 255, 255, 0.1)',
  '&::after': {
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
  },
};

function Sidebar({ isMobile, mobileOpen, drawerOpen, setDrawerOpen, handleDrawerToggle }) {
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const currentPath = location.pathname;

  // Get the user's license from state
  const { license: userLicense, appLoading } = useStateToProps((state) => ({
    license: state.app.user?.license || state.app.user?.appMetaData?.license,
    appLoading: state.global.appLoading,
  }));

  // state for multiple submenu items:
  const [openSubmenus, setOpenSubmenus] = useState({});

  // Define the current drawer width based on the drawerOpen state.
  const drawerWidthCurrent = drawerOpen ? expandedDrawerWidth : collapsedDrawerWidth;

  // Add a consistent transition duration for animations
  const drawerTransitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: theme.transitions.duration.leavingScreen,
  };

  // Function to check if user has access to a menu item
  const checkAccess = (item) => {
    // If no permission is specified, assume access is allowed
    if (!item.permission) {
      return true;
    }

    return check(userLicense, item.permission, {});
  };

  // Handle click on a menu item.
  const handleMenuClick = (item) => {
    // Check if user has access to this menu item
    const hasAccess = checkAccess(item);

    if (!hasAccess) {
      // Redirect to lock page with page title as state
      history.push({
        pathname: paths.lock,
        state: { page: item.title },
      });
      return;
    }

    if (item.subMenu) {
      if (!drawerOpen) {
        setDrawerOpen(true);
        setOpenSubmenus({ [item.key]: true }); // Only open this submenu
      } else {
        // If clicking the same menu, toggle it. If clicking a different menu, close others and open this one
        setOpenSubmenus((prev) => ({
          [item.key]: !prev[item.key],
        }));
      }
    } else {
      history.push(item.to);
    }
  };

  // Function to check if a menu item is active
  const isActive = (itemPath) => {
    // Extract the base path from itemPath (removing any query parameters)
    const basePath = itemPath.split('?')[0];

    // Special case for lock page - don't highlight parent menus
    if (currentPath === paths.lock) {
      return basePath === paths.lock && !basePath.includes('lock');
    }

    // For exact matches (ignoring query parameters)
    if (currentPath === basePath) {
      return true;
    }

    // For nested routes (if the current path starts with the item path)
    if (basePath !== '/' && basePath !== paths.lock && currentPath.startsWith(basePath)) {
      return true;
    }

    return false;
  };

  // Define the menu items with the same structure as your previous configuration.
  const menuItems = [
    {
      key: 'analytics',
      title: 'Analytics',
      icon: <Analytics className="menuIcon" fontSize="small" />,
      to: paths.analytics,
      permission: 'analytics-page:visit',
    },
    {
      key: 'participants',
      title: 'Participants',
      icon: <PeopleAltOutlinedIcon className="menuIcon" fontSize="small" />,
      to: paths.participants,
      permission: 'participants-page:visit',
    },
    {
      key: 'participants-360-view',
      title: 'Participant 360° View',
      icon: <ThreeSixtyOutlined className="menuIcon" fontSize="small" />,
      to: paths.participant360View,
      permission: 'participants-360-view-page:visit',
    },
    {
      key: 'service-bookings',
      title: 'Service Bookings',
      icon: <EventAvailableOutlinedIcon className="menuIcon" fontSize="small" />,
      to: paths.serviceBookings,
      permission: 'service-bookings-page:visit',
    },
    {
      key: 'budget',
      title: 'Budget',
      icon: <Paid className="menuIcon" fontSize="small" />,
      to: paths.budget,
      permission: 'budget-page:visit',
    },
    {
      key: 'roster-of-care',
      title: 'Roster of Care',
      icon: <ListAltRoundedIcon className="menuIcon" fontSize="small" />,
      to: paths.rosterOfCare,
      permission: 'roster-of-care-page:visit',
    },
    {
      key: 'service-delivery',
      title: 'Service Delivery',
      icon: <ListAltRoundedIcon className="menuIcon" fontSize="small" />,
      to: `${paths.serviceDelivery}?state=processing`,
      permission: 'service-delivery-page:visit',
    },
    {
      key: 'claim-management',
      title: 'Claim Management',
      icon: <PaymentsOutlinedIcon className="menuIcon" fontSize="small" />,
      to: paths.lock,
      permission: 'claim-management-page:visit',
      subMenu: [
        {
          key: 'ready-to-claim',
          title: 'Ready to Claim',
          icon: <CreditCardIcon className="menuIcon" fontSize="small" />,
          to: paths.readyToClaim,
          permission: 'ready-to-claim-page:visit',
        },
        {
          key: 'recovery-claim',
          title: 'Claim Recovery',
          icon: <PriceChangeOutlinedIcon className="menuIcon" fontSize="small" />,
          to: paths.recoveryClaim,
          permission: 'claim-recovery-page:visit',
        },
        {
          key: 'successful-claims',
          title: 'Successful Claims',
          icon: <EventAvailableOutlinedIcon className="menuIcon" fontSize="small" />,
          to: paths.successfulClaims,
          permission: 'successful-claims-page:visit',
        },
        {
          key: 'payment-cancellation',
          title: 'Cancelled Claims',
          icon: <EventAvailableOutlinedIcon className="menuIcon" fontSize="small" />,
          to: paths.paymentCancellation,
          permission: 'payment-cancellation-page:visit',
        },
        {
          key: 'claim-batch-history',
          title: 'Claim Batch History',
          icon: <AccountBalanceIcon className="menuIcon" fontSize="small" />,
          to: paths.claimBatchHistory,
          permission: 'claim-batch-history-page:visit',
        },
        {
          key: 'claims-in-error',
          title: 'Claims in Error',
          icon: <CreditCardOffIcon className="menuIcon" fontSize="small" />,
          to: paths.claimsInError,
          permission: 'claims-in-error-page:visit',
        },
        {
          key: 'remittance',
          title: 'Remittance',
          icon: <HandshakeOutlinedIcon className="menuIcon" fontSize="small" />,
          to: paths.remittance,
          permission: 'remittance-page:visit',
        },
      ],
    },
    {
      key: 'invoices',
      title: 'Invoice Management',
      icon: <DescriptionOutlinedIcon className="menuIcon" fontSize="small" />,
      to: paths.lock,
      permission: 'invoices-page:visit',
      subMenu: [
        {
          key: 'ready-to-invoice',
          title: 'Ready to Invoice',
          icon: <UploadFileOutlinedIcon className="menuIcon" fontSize="small" />,
          to: paths.readyToInvoice,
          permission: 'ready-to-invoice-page:visit',
        },
        {
          key: 'issued-invoices',
          title: 'Issued Invoices',
          icon: <TaskOutlinedIcon className="menuIcon" fontSize="small" />,
          to: paths.issuedInvoices,
          permission: 'issued-invoices-page:visit',
        },
        {
          key: 'invoices-batch-history',
          title: 'Invoice Batch History',
          icon: <Inventory2Icon className="menuIcon" fontSize="small" />,
          to: paths.invoiceBatchHistory,
          permission: 'invoice-batch-history-page:visit',
        },
        {
          key: 'invoices-in-error',
          title: 'Invoices in Error',
          icon: <SdCardAlertOutlinedIcon className="menuIcon" fontSize="small" />,
          to: paths.invoicesInError,
          permission: 'invoices-in-error-page:visit',
        },
      ],
    },
    {
      key: 'settings',
      title: 'Settings',
      icon: <SettingsIcon className="menuIcon" fontSize="small" />,
      to: paths.lock,
      permission: 'setting-page:visit',
      subMenu: [
        {
          key: 'registrations',
          title: 'NDIS Registrations',
          icon: <HowToRegOutlinedIcon className="menuIcon" fontSize="small" />,
          to: paths.registrations,
          permission: 'registrations-page:visit',
        },
        {
          key: 'product',
          title: 'Product',
          icon: <Inventory2Icon className="menuIcon" fontSize="small" />,
          to: paths.products,
          permission: 'products-page:visit',
        },
        {
          key: 'category-x',
          title: 'Category X',
          icon: <ClassOutlinedIcon className="menuIcon" fontSize="small" />,
          to: paths.categoryX,
          permission: 'category-x-page:visit',
        },
        {
          key: 'category-y',
          title: 'Category Y',
          icon: <ClassOutlinedIcon className="menuIcon" fontSize="small" />,
          to: paths.categoryY,
          permission: 'category-y-page:visit',
        },
        {
          key: 'financial-contact',
          title: 'Financial Contacts',
          icon: <ContactsIcon className="menuIcon" fontSize="small" />,
          to: paths.financialContact,
          permission: 'financial-contact-page:visit',
        },
        {
          key: 'ndis-notifications',
          title: 'NDIS Notification',
          icon: <NotificationsIcon className="menuIcon" fontSize="small" />,
          to: paths.ndisNotifications,
          permission: 'ndis-notifications-page:visit',
        },
        {
          key: 'integration',
          title: 'Integration',
          icon: <InsertLinkOutlinedIcon className="menuIcon" fontSize="small" />,
          to: paths.integration,
          permission: 'integration-page:visit',
          divider: false,
        },
      ],
    },
    {
      key: 'support-at-home',
      title: 'Support At Home',
      icon: <PeopleAltOutlinedIcon className="menuIcon" fontSize="small" />,
      to: paths.lock,
      permission: 'support-at-home-page:visit',
      subMenu: [
        {
          key: 'care-recipients',
          title: 'Care Recipients',
          icon: <PeopleAltOutlinedIcon className="menuIcon" fontSize="small" />,
          to: paths.supportAtHomeCareRecipients,
          permission: 'support-at-home-care-recipients-page:visit',
        },
        {
          key: 'care-recipient-360',
          title: 'Care Recipient 360',
          icon: <ThreeSixtyOutlined className="menuIcon" fontSize="small" />,
          to: paths.supportAtHomeCareRecipient360,
          permission: 'support-at-home-care-recipient-360-page:visit',
        },
        {
          key: 'import-transactions',
          title: 'Import Transactions',
          icon: <ReceiptLongOutlinedIcon className="menuIcon" fontSize="small" />,
          to: paths.supportAtHomeImportTransactions,
        },
        {
          key: 'transactions',
          title: 'Transactions',
          icon: <ReceiptLongOutlinedIcon className="menuIcon" fontSize="small" />,
          to: paths.supportAtHomeTransactions,
        },
        {
          key: 'sah-ready-to-claim',
          title: 'Ready to Claim',
          icon: <CreditCardIcon className="menuIcon" fontSize="small" />,
          to: paths.supportAtHomeReadyToClaim,
        },
        {
          key: 'sah-claims',
          title: 'Claims',
          icon: <PaymentsOutlinedIcon className="menuIcon" fontSize="small" />,
          to: paths.supportAtHomeClaims,
        },
        {
          key: 'sah-claim-history',
          title: 'Claim History',
          icon: <HistoryIcon className="menuIcon" fontSize="small" />,
          to: paths.supportAtHomeClaimHistory,
        },
        {
          key: 'sah-ready-to-invoice',
          title: 'Ready to Invoice',
          icon: <UploadFileOutlinedIcon className="menuIcon" fontSize="small" />,
          to: paths.supportAtHomeReadyToInvoice,
        },
        {
          key: 'sah-statement',
          title: 'Statement',
          icon: <DescriptionOutlinedIcon className="menuIcon" fontSize="small" />,
          to: paths.lock,
          permission: 'support-at-home-statement-page:visit',
        },
        {
          key: 'sah-payment',
          title: 'Payment',
          icon: <AccountBalanceWalletOutlinedIcon className="menuIcon" fontSize="small" />,
          to: paths.lock,
          permission: 'support-at-home-payment-page:visit',
        },
        {
          key: 'sah-care-management',
          title: 'Care Management',
          icon: <HealthAndSafetyOutlinedIcon className="menuIcon" fontSize="small" />,
          to: paths.lock,
          permission: 'support-at-home-care-management-page:visit',
        },
        {
          key: 'sah-care-issued-invoices',
          title: 'Issued Invoices',
          icon: <ReceiptLongOutlinedIcon className="menuIcon" fontSize="small" />,
          to: paths.lock,
          permission: 'support-at-home-issued-invoices-page:visit',
        },
        {
          key: 'sah-providers',
          title: 'Providers',
          icon: <HowToRegOutlinedIcon className="menuIcon" fontSize="small" />,
          to: paths.supportAtHomeProviders,
          permission: 'support-at-home-providers-page:visit',
        },
      ],
    },
  ];

  const renderSkeleton = () => (
    <Box p={1} justifyContent="center" display="flex" flexDirection="column" alignItems="center">
      <Skeleton height="50px" width="80%" animation="wave" sx={skeletonStyle} />
      <Skeleton height="50px" width="80%" animation="wave" sx={skeletonStyle} />
      <Skeleton height="50px" width="80%" animation="wave" sx={skeletonStyle} />
      <Skeleton height="50px" width="80%" animation="wave" sx={skeletonStyle} />
      <Skeleton height="50px" width="80%" animation="wave" sx={skeletonStyle} />
    </Box>
  );

  const drawer = (
    <div>
      <div style={{ height: '64px', alignContent: 'center' }}>
        <Logo sideMenu={drawerOpen} />
      </div>
      {!userLicense ? (
        renderSkeleton()
      ) : (
        <List>
          {menuItems.map((item) => {
            const hasAccess = checkAccess(item);

            return (
              <React.Fragment key={item.key}>
                <Tooltip
                  title={!hasAccess ? 'Upgrade your license to access this page' : ''}
                  placement="right"
                  arrow
                >
                  <div>
                    {' '}
                    {/* Wrapper div for tooltip to work with disabled items */}
                    <StyledListItem
                      button
                      onClick={() => handleMenuClick(item)}
                      isactive={isActive(item.to).toString()}
                    >
                      <ListItemIcon>
                        <StyledIcon>{item.icon}</StyledIcon>
                      </ListItemIcon>
                      {drawerOpen && <ListItemText primary={item.title} />}
                      {drawerOpen && !hasAccess && (
                        <LockIcon
                          fontSize="small"
                          sx={{ color: theme.palette.text.secondaryLight }}
                        />
                      )}
                      {drawerOpen &&
                        item.subMenu &&
                        (openSubmenus[item.key] ? <ExpandLess /> : <ExpandMore />)}
                    </StyledListItem>
                  </div>
                </Tooltip>
                {item.subMenu && (
                  <Collapse in={drawerOpen && openSubmenus[item.key]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.subMenu.map((subItem) => {
                        const hasSubItemAccess = checkAccess(subItem);

                        return (
                          <Tooltip
                            key={subItem.key}
                            title={
                              !hasSubItemAccess ? 'Upgrade your license to access this feature' : ''
                            }
                            placement="right"
                            arrow
                          >
                            <div>
                              {' '}
                              {/* Wrapper div for tooltip */}
                              <StyledSubMenuItem
                                button
                                onClick={() => {
                                  if (!hasSubItemAccess) {
                                    history.push({
                                      pathname: paths.lock,
                                      state: { page: subItem.title },
                                    });
                                  } else {
                                    history.push(subItem.to);
                                  }
                                }}
                                isactive={isActive(subItem.to).toString()}
                              >
                                <ListItemIcon>{subItem.icon}</ListItemIcon>
                                {drawerOpen && <ListItemText primary={subItem.title} />}
                                {drawerOpen && !hasSubItemAccess && (
                                  <LockIcon
                                    fontSize="small"
                                    sx={{ color: theme.palette.text.secondaryLight }}
                                  />
                                )}
                              </StyledSubMenuItem>
                            </div>
                          </Tooltip>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
                {item.divider && <Divider />}
              </React.Fragment>
            );
          })}
        </List>
      )}
    </div>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { sm: drawerWidthCurrent },
        flexShrink: { sm: 0 },
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: drawerTransitionDuration.enter,
        }),
      }}
      aria-label="mailbox folders"
    >
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            // backgroundColor: theme.palette.background.darker,
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: expandedDrawerWidth,
              backgroundColor: theme.palette.background.darker,
            },
          }}
        >
          {drawer}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidthCurrent,
              backgroundColor: theme.palette.background.darker,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: drawerTransitionDuration.enter,
              }),
              overflowX: 'hidden',
            },
          }}
        >
          {drawer}
        </Drawer>
      )}
    </Box>
  );
}

export default Sidebar;
